import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertCircle, CheckCircle, Eye, EyeOff, Loader2 } from "lucide-react";

import logo from "../../assets/nhclogo.png";
import { getApiUrl } from "../../utils/api";

const SetPasswordPage = () => {
    let id = useParams();
    const initialState = {
        password: "",
        confirmPassword: "",
        passwordMatch: null,
        passwordToken: id.token,
        success: false
    };

    useEffect(() => {
        verifyToken(id.token);
    }, []);

    const [user, setUser] = useState({ ...initialState });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const apiUrl = getApiUrl();

    const verifyToken = async (token) => {
        try {
            const response = await fetch(`${apiUrl}/users/validatetoken/${token}`);
            const data = await response.json();

            if (!data.success) {
                window.location.href = "/invalid";
            }
        } catch (error) {
            console.log(error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const requestOptions = {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    id: user.id,
                    password: user.password,
                    confirmPassword: user.confirmPassword,
                    passwordToken: id.token
                }),
            };
            const response = await fetch(
                `${apiUrl}/users/setpassword`,
                requestOptions
            );
            const data = await response.json();
            if (data.message) {
                if (data.success) {
                    setUser({ ...user, success: true });
                    setTimeout(() => {
                        window.location.href = "/signin";
                    }, 1000);
                } else {
                    setUser({ ...user, message: data.message, success: false });
                }
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        e.target.name === "password"
            ? setUser({ ...user, password: e.target.value })
            : setUser({ ...user, confirmPassword: e.target.value });
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary to-primary-dark p-4">
            <div className="w-full max-w-sm bg-white rounded-2xl shadow-modal p-8 md:p-10">
                {/* logo */}
                <div className="flex justify-center mb-6">
                    <img src={logo} alt="National HealthClaim" className="w-56" />
                </div>

                {/* title */}
                <h2 className="text-center text-lg font-semibold text-gray-900 mb-6">
                    Enter your password
                </h2>

                {/* password mismatch error */}
                {user.passwordMatch === false && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span>Passwords do not match</span>
                    </div>
                )}

                {/* success message */}
                {user.message != null && user.success && (
                    <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl text-green-600 text-sm flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 flex-shrink-0" />
                        <span>Password has been successfully set. Please login.</span>
                    </div>
                )}

                {/* error message */}
                {user.message != null && !user.success && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span>{user.message}</span>
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    {/* password field */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                value={user.password}
                                onChange={handleChange}
                                placeholder="Enter your password"
                                required
                                className="w-full p-4 pr-12 border border-gray-200 rounded-xl text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all duration-200"
                            />
                            <button
                                type="button"
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                onMouseDown={() => setShowPassword(true)}
                                onMouseUp={() => setShowPassword(false)}
                                onMouseLeave={() => setShowPassword(false)}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? (
                                    <Eye className="w-5 h-5" />
                                ) : (
                                    <EyeOff className="w-5 h-5" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* confirm password field */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Confirm Password
                        </label>
                        <div className="relative">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                name="confirmPassword"
                                value={user.confirmPassword}
                                onChange={handleChange}
                                placeholder="Confirm your password"
                                required
                                className="w-full p-4 pr-12 border border-gray-200 rounded-xl text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all duration-200"
                            />
                            <button
                                type="button"
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                                onMouseDown={() => setShowConfirmPassword(true)}
                                onMouseUp={() => setShowConfirmPassword(false)}
                                onMouseLeave={() => setShowConfirmPassword(false)}
                                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                            >
                                {showConfirmPassword ? (
                                    <Eye className="w-5 h-5" />
                                ) : (
                                    <EyeOff className="w-5 h-5" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* submit button */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-primary text-white py-4 px-6 rounded-full font-semibold text-base shadow-sm hover:bg-primary-dark active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                        {loading ? "Submitting..." : "Submit"}
                    </button>
                </form>

                {/* return to login link */}
                <Link
                    to="/signin"
                    className="block mt-4 text-center text-sm text-primary hover:text-primary-dark transition-colors"
                >
                    Return to login
                </Link>
            </div>
        </div>
    );
};

export default SetPasswordPage;
